import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import axiosInstance from '../../config/axios.config';
import ConnectionService from '../../services/agentProfile/ConnectionService';
import { fetchPageData } from '../../services/pageDataService';
import { useNotifications } from '../../contexts/NotificationContext';

import { FILTER_PRESETS } from './utils';

import ManageNetworkSidebar from './sections/ManageNetworkSidebar';
import FindAgentsBar from './sections/FindAgentsBar';
import PendingInvitationsCard from './sections/PendingInvitationsCard';
import PeopleYouMayKnowCard from './sections/PeopleYouMayKnowCard';
import MyConnectionsCard from './sections/MyConnectionsCard';

/**
 * MyNetwork — v2.0 hub-style dashboard.
 *
 * Production-grade rebuild of /app/my-network using the social prototype's
 * polished outer shell + halo avatars + AvatarGroup face piles + pill
 * filter chips, all wired to the existing ConnectionService.
 *
 *   Real APIs:
 *     • ConnectionService.getStatistics
 *     • ConnectionService.getConnections({ status: 'accepted' })
 *     • ConnectionService.getConnections({ status: 'pending', type: 'received' | 'sent' })
 *     • ConnectionService.getSuggestions
 *     • ConnectionService.sendRequest / acceptRequest / rejectRequest / removeConnection
 *
 *   Right-rail widgets are rendered INLINE (not via the existing
 *   ProfileViewers / NetworkSuggestions / NetworkSearch components) so the
 *   layout matches the prototype pixel-for-pixel:
 *     • "Who viewed your profile" — fetches /profile/view-statistics
 *       and /profile/viewers?limit=4&days=90 directly here
 *     • "People you may know" — top 3 from internal+external suggestion
 *       endpoints
 *     • Suggestion cards in the main column — same source, with
 *       dismissable cards and SelectButton scope toggle
 */

// ═════════════════════════════════════════════════════════════════════════
// MAIN
// ═════════════════════════════════════════════════════════════════════════
const MyNetwork = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    // ─── Network data state (preserves production semantics) ────────────
    const [stats, setStats] = useState(null);
    /* `connections` is now a server-paginated, accumulated list — gets
       reset and refetched whenever search/sort/filter changes, and grows
       page-by-page as the user infinite-scrolls. */
    const [connections, setConnections] = useState([]);
    const [connectionsPage, setConnectionsPage] = useState(0);
    const [connectionsLastPage, setConnectionsLastPage] = useState(1);
    const [connectionsTotal, setConnectionsTotal] = useState(0);
    const [connectionsLoading, setConnectionsLoading] = useState(false);    // initial / reset
    const [connectionsLoadingMore, setConnectionsLoadingMore] = useState(false); // append next page
    /* Pending invitations — both Received and Sent are server-paginated
       just like My Connections. Each tab maintains its own page state +
       infinite-scroll sentinel so loading 50-100 at once is gone. */
    const [pendingReceived, setPendingReceived] = useState([]);
    const [pendingReceivedPage, setPendingReceivedPage] = useState(0);
    const [pendingReceivedLastPage, setPendingReceivedLastPage] = useState(1);
    const [pendingReceivedTotal, setPendingReceivedTotal] = useState(0);
    const [pendingReceivedLoading, setPendingReceivedLoading] = useState(false);
    const [pendingReceivedLoadingMore, setPendingReceivedLoadingMore] = useState(false);
    const pendingReceivedAbortRef = useRef(null);
    const pendingReceivedSentinelRef = useRef(null);

    const [pendingSent, setPendingSent] = useState([]);
    const [pendingSentPage, setPendingSentPage] = useState(0);
    const [pendingSentLastPage, setPendingSentLastPage] = useState(1);
    const [pendingSentTotal, setPendingSentTotal] = useState(0);
    const [pendingSentLoading, setPendingSentLoading] = useState(false);
    const [pendingSentLoadingMore, setPendingSentLoadingMore] = useState(false);
    const pendingSentAbortRef = useRef(null);
    const pendingSentSentinelRef = useRef(null);

    /* Expand-to-scroll state (per tab — independent).
       Compact: shows first 5 items + "Show more" button.
       Expanded: scroll container with capped height + internal
       infinite-scroll, ScrollTop FAB anchored to container. Auto-collapses
       when the user scrolls the page past the entire card. */
    const [receivedExpanded, setReceivedExpanded] = useState(false);
    const [sentExpanded, setSentExpanded] = useState(false);
    const receivedScrollRef = useRef(null);
    const sentScrollRef = useRef(null);
    const pendingCardRef = useRef(null);

    /* Same compact/expanded pattern for the My Connections list so the
       connections section stays self-contained (its own scroll, its own
       ScrollTop FAB) — matches the invitations behavior on every device. */
    const [connectionsExpanded, setConnectionsExpanded] = useState(false);
    const connectionsScrollRef = useRef(null);
    const connectionsCardRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [noProfile, setNoProfile] = useState(false);
    const [networkBlocked, setNetworkBlocked] = useState(null);
    /* AbortController + sentinel ref for infinite-scroll. The controller
       lets us cancel a stale page request when the user types fast or
       toggles a filter mid-fetch (so a slow old response doesn't append
       on top of a new one). */
    const connectionsAbortRef = useRef(null);
    const connectionsSentinelRef = useRef(null);

    /* Sticky offsets — measured at runtime so the search bar + sidebar
       both clear whatever chrome is fixed at the top (mobile compact
       topbar can be a different height than desktop, future redesigns
       might add a breadcrumb bar, etc.). Updated on resize. */
    const searchBarWrapperRef = useRef(null);
    const [stickyOffsets, setStickyOffsets] = useState({
        searchTop:  64,   // default = 4rem topbar
        sidebarTop: 144,  // default = topbar + ~5rem search bar
    });

    useEffect(() => {
        const measure = () => {
            const topbar = document.querySelector('.layout-topbar');
            const topbarH = topbar ? topbar.getBoundingClientRect().height : 64;
            const searchH = searchBarWrapperRef.current
                ? searchBarWrapperRef.current.getBoundingClientRect().height
                : 80;
            setStickyOffsets({
                searchTop:  Math.round(topbarH),
                sidebarTop: Math.round(topbarH + searchH + 8), // small gap
            });
        };
        measure();
        // Re-measure once after first paint in case fonts shift heights.
        const t = setTimeout(measure, 120);
        window.addEventListener('resize', measure);
        return () => {
            clearTimeout(t);
            window.removeEventListener('resize', measure);
        };
    }, []);

    // ─── Right-rail widget data (loaded inline, no helper components) ───
    const [viewerStats, setViewerStats] = useState(null);
    const [profileViewers, setProfileViewers] = useState([]);
    const [internalSuggestions, setInternalSuggestions] = useState([]);
    const [externalSuggestions, setExternalSuggestions] = useState([]);
    const [dismissedSuggestions, setDismissedSuggestions] = useState(new Set());

    // ─── Cross-tenant agent search (top "Find agents" bar) ──────────────
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchedQuery, setSearchedQuery] = useState('');

    // ─── UI state ────────────────────────────────────────────────────────
    const [findSearch, setFindSearch] = useState('');
    const [connectionsSearch, setConnectionsSearch] = useState('');
    const [activeFilters, setActiveFilters] = useState([]);
    const [sortBy, setSortBy] = useState('recent');
    const [viewMode, setViewMode] = useState('list');
    /* Tab inside the Pending Invitations card.
       0 = Received (default), 1 = Sent. The sidebar's "Sent Requests"
       stat row deep-links here by setting this to 1 and scrolling. */
    const [pendingTabIndex, setPendingTabIndex] = useState(0);
    const [suggestionScope, setSuggestionScope] = useState('internal');

    const { markConnectionsViewed, fetchPendingConnectionsCount } = useNotifications();

    // ─── Mount: load all five lists in parallel + clear unread badge ────
    useEffect(() => {
        loadNetworkData();
        markConnectionsViewed();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Cross-tenant agent search (debounced) ──────────────────────────
    // 350ms after the user stops typing in the top "Find agents" bar,
    // hit /leadflow/network/search?q=… and show the results inline
    // between the find bar and the pending-invitations section.
    useEffect(() => {
        const q = findSearch.trim();
        if (q.length < 2) {
            setSearchResults([]);
            setSearchedQuery('');
            setSearching(false);
            return undefined;
        }
        let cancelled = false;
        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const params = new URLSearchParams();
                params.append('q', q);
                params.append('limit', 10);
                params.append('page', 1);
                const response = await axiosInstance.get(
                    `/leadflow/network/search?${params.toString()}`
                );
                if (cancelled) return;
                if (response.data?.success) {
                    setSearchResults(response.data.data?.results || []);
                    setSearchedQuery(q);
                } else {
                    setSearchResults([]);
                    setSearchedQuery(q);
                }
            } catch (error) {
                if (!cancelled) {
                    setSearchResults([]);
                    setSearchedQuery(q);
                }
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 350);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [findSearch]);

    const loadNetworkData = async () => {
        try {
            setLoading(true);
            setNetworkBlocked(null);
            setNoProfile(false);

            // One request for all five (#162). The three connection lists
            // (accepted / pending received / pending sent) are NOT in it — each
            // is server-paginated via its own fetch* function fired from a
            // separate effect on mount + on filter / sort / search / tab
            // change. Keeps initial paint fast and scales to thousands of
            // rows via infinite scroll.
            //
            // The aggregate keeps allSettled's key property — one slow or
            // failing suggestion query must not blank the page — by reporting
            // per section instead of rejecting.
            const page = await fetchPageData('my-network');

            // The Lending-Network gate and the missing-profile state used to
            // arrive as a REJECTED promise and were caught below. Inside an
            // aggregate they arrive as a section error, so they are recognised
            // here instead; the catch block still covers a transport failure of
            // the aggregate request itself.
            const gate = ['statistics', 'suggestions_internal', 'suggestions_external']
                .map((name) => page.meta(name))
                .find((m) => m?.http_status === 403 && String(m?.code || '').startsWith('ln_'));
            if (gate) {
                setNetworkBlocked({ code: gate.code, message: gate.message, reason: gate.reason });
                return;
            }

            const profileMissing = ['statistics', 'view_statistics']
                .map((name) => page.meta(name))
                .some((m) => m?.http_status === 400 && String(m?.message || '').includes('agent profile'));
            if (profileMissing) {
                setNoProfile(true);
                return;
            }

            const statsRes = page.section('statistics');
            const viewStatsRes = page.section('view_statistics');
            const viewersRes = page.section('profile_viewers');
            const internalSuggRes = page.section('suggestions_internal');
            const externalSuggRes = page.section('suggestions_external');
            const failed = [];

            if (statsRes.success) {
                setStats(statsRes.data);
            } else {
                failed.push('statistics');
            }

            if (viewStatsRes.success) {
                setViewerStats(viewStatsRes.data || null);
            }
            if (viewersRes.success) {
                setProfileViewers(viewersRes.data || []);
            }
            if (internalSuggRes.success) {
                setInternalSuggestions(internalSuggRes.data || []);
            }
            if (externalSuggRes.success) {
                setExternalSuggestions(externalSuggRes.data || []);
            }

            if (failed.length > 0 && failed.length < 4) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Partial load',
                    detail: `Some data couldn't be loaded: ${failed.join(', ')}.`,
                    life: 4000,
                });
            }
        } catch (error) {
            // LN gate (403 with ln_* code) → show banner, stop everything else
            if (error.response?.status === 403 && error.response?.data?.code?.startsWith('ln_')) {
                setNetworkBlocked({
                    code: error.response.data.code,
                    message: error.response.data.message,
                    reason: error.response.data.reason,
                });
                return;
            }
            // No agent profile → show "create profile" empty state
            if (
                error.response?.status === 400 &&
                error.response?.data?.message?.includes('agent profile')
            ) {
                setNoProfile(true);
                return;
            }
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to load network data',
                life: 4000,
            });
        } finally {
            setLoading(false);
        }
    };

    /* ─── Server-side pending invitations (received) fetch ────────────── */
    const fetchPendingReceivedPage = useCallback(async (page, reset = false) => {
        if (pendingReceivedAbortRef.current) {
            try { pendingReceivedAbortRef.current.abort(); } catch (_) { /* noop */ }
        }
        const ctrl = new AbortController();
        pendingReceivedAbortRef.current = ctrl;
        if (reset) setPendingReceivedLoading(true);
        else       setPendingReceivedLoadingMore(true);
        try {
            const res = await ConnectionService.getConnections(
                { status: 'pending', type: 'received', page, per_page: 10 },
                { signal: ctrl.signal }
            );
            if (ctrl.signal.aborted) return;
            if (res?.success) {
                const items   = res.data?.data || [];
                const curPage = res.data?.current_page || page;
                const last    = res.data?.last_page    || 1;
                const total   = res.data?.total        || items.length;
                setPendingReceived((prev) => reset ? items : [...prev, ...items]);
                setPendingReceivedPage(curPage);
                setPendingReceivedLastPage(last);
                setPendingReceivedTotal(total);
            }
        } catch (err) {
            // Silent — invitations card simply stays empty on failure.
        } finally {
            if (!ctrl.signal.aborted) {
                if (reset) setPendingReceivedLoading(false);
                else       setPendingReceivedLoadingMore(false);
            }
        }
    }, []);

    /* ─── Server-side pending invitations (sent) fetch ─────────────────── */
    const fetchPendingSentPage = useCallback(async (page, reset = false) => {
        if (pendingSentAbortRef.current) {
            try { pendingSentAbortRef.current.abort(); } catch (_) { /* noop */ }
        }
        const ctrl = new AbortController();
        pendingSentAbortRef.current = ctrl;
        if (reset) setPendingSentLoading(true);
        else       setPendingSentLoadingMore(true);
        try {
            const res = await ConnectionService.getConnections(
                { status: 'pending', type: 'sent', page, per_page: 10 },
                { signal: ctrl.signal }
            );
            if (ctrl.signal.aborted) return;
            if (res?.success) {
                const items   = res.data?.data || [];
                const curPage = res.data?.current_page || page;
                const last    = res.data?.last_page    || 1;
                const total   = res.data?.total        || items.length;
                setPendingSent((prev) => reset ? items : [...prev, ...items]);
                setPendingSentPage(curPage);
                setPendingSentLastPage(last);
                setPendingSentTotal(total);
            }
        } catch (err) {
            // Silent.
        } finally {
            if (!ctrl.signal.aborted) {
                if (reset) setPendingSentLoading(false);
                else       setPendingSentLoadingMore(false);
            }
        }
    }, []);

    /* Initial load — only Received fires on mount. Sent is hidden behind
       a tab click most users never make, so we defer it to save one HTTP
       request per page load. The "Sent (N)" tab label still shows the
       correct count via the stats fallback in statValues below. */
    useEffect(() => {
        fetchPendingReceivedPage(1, true);
    }, [fetchPendingReceivedPage]);

    /* Lazy Sent fetch — fires page 1 the FIRST time the Sent tab becomes
       active (either by a tab click or the auto-shift below). After that
       the IntersectionObserver below handles further pages. */
    useEffect(() => {
        if (pendingTabIndex !== 1) return;
        if (pendingSentPage > 0) return;       // already fetched
        if (pendingSentLoading) return;        // currently in flight
        fetchPendingSentPage(1, true);
    }, [pendingTabIndex, pendingSentPage, pendingSentLoading, fetchPendingSentPage]);

    /* Auto-shift to Sent tab if Received is empty AND stats reports there
       are pending sent requests. Runs once. Uses `stats.pending_sent` from
       the getStatistics() call (which is fetched in parallel by
       loadNetworkData) so we don't have to fetch the Sent list to know it
       has items. */
    const initialTabSetRef = useRef(false);
    useEffect(() => {
        if (initialTabSetRef.current) return;
        if (pendingReceivedPage === 0 || pendingReceivedLoading) return;
        // Need stats to know pending_sent count. If stats is still null
        // (loading or failed) we wait — when it resolves this effect re-runs.
        if (stats === null) return;
        initialTabSetRef.current = true;
        if (pendingReceivedTotal === 0 && (stats.pending_sent ?? 0) > 0) {
            setPendingTabIndex(1);
        }
    }, [pendingReceivedPage, pendingReceivedLoading, pendingReceivedTotal, stats]);

    /* Has-more flags for both invitation tabs. */
    const pendingReceivedHasMore = pendingReceivedPage > 0 && pendingReceivedPage < pendingReceivedLastPage;
    const pendingSentHasMore     = pendingSentPage > 0     && pendingSentPage < pendingSentLastPage;

    /* IntersectionObserver — Received tab. Only fires when:
         - Received tab is active
         - The tab is in expanded mode (sentinel only renders then)
         - The container ref exists (used as observer root)
       This way compact mode doesn't auto-fetch — user must opt in. */
    useEffect(() => {
        if (pendingTabIndex !== 0) return undefined;
        if (!receivedExpanded) return undefined;
        const el = pendingReceivedSentinelRef.current;
        if (!el) return undefined;
        const obs = new IntersectionObserver((entries) => {
            const e = entries[0];
            if (!e?.isIntersecting) return;
            if (pendingReceivedLoading || pendingReceivedLoadingMore) return;
            if (!pendingReceivedHasMore) return;
            fetchPendingReceivedPage(pendingReceivedPage + 1, false);
        }, {
            root: receivedScrollRef.current,        // ← internal container, not viewport
            rootMargin: '0px 0px 200px 0px',
        });
        obs.observe(el);
        return () => obs.disconnect();
    }, [pendingTabIndex, receivedExpanded, pendingReceivedHasMore, pendingReceivedLoading, pendingReceivedLoadingMore, pendingReceivedPage, fetchPendingReceivedPage]);

    /* IntersectionObserver — Sent tab. */
    useEffect(() => {
        if (pendingTabIndex !== 1) return undefined;
        if (!sentExpanded) return undefined;
        const el = pendingSentSentinelRef.current;
        if (!el) return undefined;
        const obs = new IntersectionObserver((entries) => {
            const e = entries[0];
            if (!e?.isIntersecting) return;
            if (pendingSentLoading || pendingSentLoadingMore) return;
            if (!pendingSentHasMore) return;
            fetchPendingSentPage(pendingSentPage + 1, false);
        }, {
            root: sentScrollRef.current,
            rootMargin: '0px 0px 200px 0px',
        });
        obs.observe(el);
        return () => obs.disconnect();
    }, [pendingTabIndex, sentExpanded, pendingSentHasMore, pendingSentLoading, pendingSentLoadingMore, pendingSentPage, fetchPendingSentPage]);

    /* Auto-collapse on scroll-past — when the user scrolls the PAGE past
       the entire card (card.bottom < 0), reset both expansion states. The
       user comes back to a clean compact view if they scroll up again.
       rAF-throttled to keep it cheap. Only listens while at least one tab
       is expanded — no overhead in the default case. */
    useEffect(() => {
        if (!receivedExpanded && !sentExpanded) return undefined;
        const card = pendingCardRef.current;
        if (!card) return undefined;
        let rafId = null;
        const onScroll = () => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const rect = card.getBoundingClientRect();
                if (rect.bottom < 0) {
                    setReceivedExpanded(false);
                    setSentExpanded(false);
                }
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [receivedExpanded, sentExpanded]);

    /* ─── Server-side connections fetch (paginated, with abort) ────────
       fetchConnectionsPage(page, reset?) — single function for both the
       reset-to-page-1 case (filter / sort / search changed) and the
       append-next-page case (user scrolled to the bottom).

       Always cancels any in-flight previous request via AbortController so
       a slow stale response can't append on top of a newer one. */
    const fetchConnectionsPage = useCallback(async (page, reset = false) => {
        // Cancel any prior in-flight request.
        if (connectionsAbortRef.current) {
            try { connectionsAbortRef.current.abort(); } catch (_) { /* noop */ }
        }
        const controller = new AbortController();
        connectionsAbortRef.current = controller;

        if (reset) setConnectionsLoading(true);
        else       setConnectionsLoadingMore(true);

        // Translate the user-facing filter chips into backend params:
        //   - city chips         → filter_cities[]
        //   - product-type chips → filter_specializations[]
        const cities = [];
        const specs = [];
        for (const label of activeFilters) {
            const f = FILTER_PRESETS.find((x) => x.label === label);
            if (!f) continue;
            if (f.match?.city)         cities.push(f.match.city);
            if (f.match?.product_type) specs.push(f.match.product_type);
        }

        const params = {
            status: 'accepted',
            page,
            per_page: 10,
        };
        const q = connectionsSearch.trim();
        if (q)              params.search = q;
        if (sortBy)         params.sort = sortBy;
        if (cities.length)  params.filter_cities = cities.join(',');
        if (specs.length)   params.filter_specializations = specs.join(',');

        try {
            const res = await ConnectionService.getConnections(params, { signal: controller.signal });
            if (controller.signal.aborted) return;
            if (res?.success) {
                const items   = res.data?.data || [];
                const curPage = res.data?.current_page || page;
                const last    = res.data?.last_page    || 1;
                const total   = res.data?.total        || items.length;
                setConnections((prev) => reset ? items : [...prev, ...items]);
                setConnectionsPage(curPage);
                setConnectionsLastPage(last);
                setConnectionsTotal(total);
            }
        } catch (err) {
            if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Couldn\'t load connections',
                    life: 3000,
                });
            }
        } finally {
            if (!controller.signal.aborted) {
                if (reset) setConnectionsLoading(false);
                else       setConnectionsLoadingMore(false);
            }
        }
    }, [connectionsSearch, sortBy, activeFilters]);

    /* Reset-to-page-1 effect.
       Search input is debounced 350ms (so fast typing doesn't fire a request
       per keystroke). Sort dropdown + filter chips are immediate (no debounce).
       Both code paths reset list to empty + refetch page 1 with new params. */
    useEffect(() => {
        const debounce = setTimeout(() => {
            fetchConnectionsPage(1, true);
        }, 350);
        return () => clearTimeout(debounce);
    }, [connectionsSearch, sortBy, activeFilters, fetchConnectionsPage]);

    /* Has-more flag for the IntersectionObserver. */
    const connectionsHasMore = connectionsPage > 0 && connectionsPage < connectionsLastPage;

    /* IntersectionObserver — when the sentinel below the last connection
       row enters the viewport, fetch the next page.
       Only fires when the connections section is in expanded mode (sentinel
       only renders then). The `root` switches between the internal scroll
       container (expanded) and the viewport (fallback) so auto-load reacts
       to the right scroll context. */
    useEffect(() => {
        if (!connectionsExpanded) return undefined;
        const el = connectionsSentinelRef.current;
        if (!el) return undefined;
        const observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (!entry?.isIntersecting) return;
            if (connectionsLoading || connectionsLoadingMore) return;
            if (!connectionsHasMore) return;
            fetchConnectionsPage(connectionsPage + 1, false);
        }, {
            root: connectionsScrollRef.current,
            rootMargin: '0px 0px 200px 0px',
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [connectionsExpanded, connectionsHasMore, connectionsLoading, connectionsLoadingMore, connectionsPage, fetchConnectionsPage]);

    /* Auto-collapse the connections section when the user scrolls past
       the entire card. rAF-throttled, only listens while expanded. */
    useEffect(() => {
        if (!connectionsExpanded) return undefined;
        const card = connectionsCardRef.current;
        if (!card) return undefined;
        let rafId = null;
        const onScroll = () => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const rect = card.getBoundingClientRect();
                if (rect.bottom < 0) {
                    setConnectionsExpanded(false);
                }
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [connectionsExpanded]);

    // ─── Action handlers ────────────────────────────────────────────────
    const handleAccept = async (connectionId) => {
        // Capture the invitation object BEFORE the network round-trip so we
        // still have access to other_party data after the optimistic remove.
        const invitation = pendingReceived.find((c) => c.id === connectionId);
        try {
            setActionLoading(connectionId);
            const response = await ConnectionService.acceptRequest(connectionId);
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Connection accepted',
                    life: 2000,
                });
                // Optimistic update — pending tab loses one, connections gains
                // one (prepended), counts adjusted. No cascading refetch.
                setPendingReceived((prev) => prev.filter((c) => c.id !== connectionId));
                setPendingReceivedTotal((prev) => Math.max(0, prev - 1));
                if (invitation) {
                    const nowIso = new Date().toISOString();
                    const newConnection = {
                        ...invitation,
                        status: 'accepted',
                        responded_at: nowIso,
                        connected_at: nowIso,
                        accepted_at: nowIso,
                    };
                    setConnections((prev) => [newConnection, ...prev]);
                    setConnectionsTotal((prev) => prev + 1);
                }
                // Topbar pending badge — small targeted call, NOT a full refetch.
                fetchPendingConnectionsCount();
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to accept connection',
                life: 3000,
            });
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (connectionId) => {
        try {
            setActionLoading(connectionId);
            const response = await ConnectionService.rejectRequest(connectionId);
            if (response.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Invitation declined',
                    life: 2000,
                });
                // Optimistic update — only the received tab is affected.
                setPendingReceived((prev) => prev.filter((c) => c.id !== connectionId));
                setPendingReceivedTotal((prev) => Math.max(0, prev - 1));
                fetchPendingConnectionsCount();
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to reject connection',
                life: 3000,
            });
        } finally {
            setActionLoading(null);
        }
    };

    // (Removing a connection now lives on the profile-view page —
    // /app/profile/:profileId — so we no longer expose that action from
    // the network page row menu.)

    const handleCancelSent = (connectionId) => {
        confirmDialog({
            message: 'Withdraw this invitation?',
            header: 'Withdraw invitation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const response = await ConnectionService.removeConnection(connectionId);
                    if (response.success) {
                        toast.current?.show({
                            severity: 'info',
                            summary: 'Invitation withdrawn',
                            life: 2000,
                        });
                        setPendingSent((prev) => prev.filter((s) => s.id !== connectionId));
                        // Keep the Sent tab label "(N)" in sync with the
                        // optimistic local state — server-reported total
                        // would otherwise lag until the next refetch.
                        setPendingSentTotal((prev) => Math.max(0, prev - 1));
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to withdraw invitation',
                        life: 3000,
                    });
                }
            },
        });
    };

    const toggleFilter = (label) =>
        setActiveFilters((prev) =>
            prev.includes(label) ? prev.filter((f) => f !== label) : [...prev, label]
        );
    const clearFilters = () => setActiveFilters([]);

    // ─── Suggestions: connect + dismiss ─────────────────────────────────
    const handleSuggestionConnect = async (profileId, displayName) => {
        try {
            setActionLoading(profileId);
            const response = await ConnectionService.sendRequest({
                receiver_profile_id: parseInt(profileId, 10),
                connection_message: null,
            });
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: `Request sent${displayName ? ` to ${displayName.split(' ')[0]}` : ''}`,
                    life: 2200,
                });

                // Removes the tile from the suggestions grid via the visibleSuggestions memo.
                setDismissedSuggestions((prev) => new Set([...prev, profileId]));

                /* Build an optimistic Sent-invitation stub so:
                     • the Sent tab header count "(N)" goes up immediately
                     • the Sent tab content stays consistent if the user opens
                       it before the next pendingSent fetch
                   (The search-results CTA flip is handled separately below by
                   updating that row's connection_status — the backend now
                   returns this field per result so we don't need a memoised
                   lookup Set anymore.)
                   Look up the suggestion's full record from whichever list it
                   came from — internal/external suggestions or the cross-tenant
                   search results — so the row carries proper headline/photo. */
                const candidate = [...internalSuggestions, ...externalSuggestions, ...searchResults]
                    .find((s) => (s.profile_id || s.id) === profileId);
                const otherParty = candidate
                    ? {
                        profile_id:             candidate.profile_id || candidate.id,
                        full_name:              candidate.full_name || displayName,
                        headline:               candidate.headline,
                        current_designation:    candidate.current_designation,
                        current_company:        candidate.current_company,
                        location_city:          candidate.location_city,
                        profile_photo_url_full: candidate.profile_photo_url_full,
                        profile_photo_url:      candidate.profile_photo_url,
                        profile_photo:          candidate.profile_photo,
                        avatar:                 candidate.avatar,
                        photo_url:              candidate.photo_url,
                    }
                    : { profile_id: profileId, full_name: displayName };

                const nowIso = new Date().toISOString();
                const stub = {
                    id:           response.data?.id ?? `temp-${Date.now()}`,
                    status:       'pending',
                    other_party:  otherParty,
                    requested_at: nowIso,
                    created_at:   nowIso,
                };

                setPendingSent((prev) => [stub, ...prev]);
                setPendingSentTotal((prev) => prev + 1);

                /* If the click came from the search-results panel, flip that
                   row's `connection_status` locally so the CTA changes from
                   "Connect" to "Request sent" without re-querying. */
                setSearchResults((prev) =>
                    prev.map((r) =>
                        (r.id || r.profile_id) === profileId
                            ? { ...r, connection_status: 'pending_sent' }
                            : r
                    )
                );
            }
        } catch (error) {
            const errors = error.response?.data?.errors;
            const detail = errors
                ? Object.values(errors).flat().join(', ')
                : error.response?.data?.message || 'Failed to send connection request';
            toast.current?.show({ severity: 'error', summary: 'Error', detail, life: 4000 });
        } finally {
            setActionLoading(null);
        }
    };

    const handleSuggestionDismiss = (profileId) =>
        setDismissedSuggestions((prev) => new Set([...prev, profileId]));

    // Visible suggestions = current scope filtered by dismissals + by find search
    const visibleSuggestions = useMemo(() => {
        const list = suggestionScope === 'internal' ? internalSuggestions : externalSuggestions;
        const q = findSearch.trim().toLowerCase();
        return list
            .filter((s) => !dismissedSuggestions.has(s.profile_id || s.id))
            .filter((s) => {
                if (!q) return true;
                return (
                    (s.full_name || '').toLowerCase().includes(q) ||
                    (s.headline || '').toLowerCase().includes(q) ||
                    (s.location_city || '').toLowerCase().includes(q) ||
                    (s.current_company || '').toLowerCase().includes(q)
                );
            });
    }, [internalSuggestions, externalSuggestions, suggestionScope, dismissedSuggestions, findSearch]);

    // Top-3 mini list for the right-rail "People you may know" widget
    const topSuggestions = useMemo(() => {
        const list = [...internalSuggestions, ...externalSuggestions].filter(
            (s) => !dismissedSuggestions.has(s.profile_id || s.id)
        );
        return list.slice(0, 3);
    }, [internalSuggestions, externalSuggestions, dismissedSuggestions]);

    // ─── Stat card click handlers ────────────────────────────────────────
    const scrollTo = (id) =>
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // ─── Render: blocked state (LN gate) ────────────────────────────────
    if (networkBlocked) {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid">
                    <div className="col-12">
                        <Card>
                            <Message
                                severity="warn"
                                content={
                                    <div>
                                        <div className="font-semibold text-900 mb-1">
                                            Lending Network access pending
                                        </div>
                                        <div className="text-600 text-sm line-height-3">
                                            {networkBlocked.message ||
                                                'Your network activity is limited while your access is being reviewed.'}
                                        </div>
                                    </div>
                                }
                                className="w-full"
                            />
                        </Card>
                    </div>
                </div>
            </>
        );
    }

    // ─── Render: no profile ─────────────────────────────────────────────
    if (noProfile) {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid">
                    <div className="col-12">
                        <Card>
                            <div className="flex flex-column align-items-center text-center py-5 gap-3">
                                <div
                                    className="flex align-items-center justify-content-center border-circle"
                                    style={{ width: '4rem', height: '4rem', backgroundColor: 'var(--primary-100)' }}
                                >
                                    <i className="pi pi-user-plus text-primary" style={{ fontSize: '1.6rem' }} />
                                </div>
                                <div>
                                    <h3 className="text-900 font-bold m-0 mb-2">Create your agent profile first</h3>
                                    <p className="text-600 text-sm m-0 line-height-3" style={{ maxWidth: '32rem' }}>
                                        You need an agent profile before you can build your network.
                                    </p>
                                </div>
                                <Button
                                    label="Create Profile"
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    onClick={() => navigate('/app/create-profile')}
                                />
                            </div>
                        </Card>
                    </div>
                </div>
            </>
        );
    }

    // ─── Render: loading skeleton ───────────────────────────────────────
    if (loading) {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid">
                    <div className="col-12">
                        <Skeleton width="40%" height="2rem" className="mb-3" />
                        <div className="grid mb-3">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="col-6 md:col-3">
                                    <Skeleton height="5rem" />
                                </div>
                            ))}
                        </div>
                        <Skeleton height="3rem" className="mb-3" />
                        <Skeleton height="20rem" />
                    </div>
                </div>
            </>
        );
    }

    // ─── Stats — local counts win after first paginated fetch.
    // The `stats` API call is a snapshot from initial mount; once any of
    // the paginated lists has fetched its first page (page > 0), the local
    // total is the source of truth (it tracks our optimistic updates after
    // accept / reject / withdraw). Falling back to `stats` only covers the
    // brief window between mount and the first page returning.
    const statValues = {
        connections:          connectionsPage     > 0 ? connectionsTotal     : (stats?.total_connections ?? 0),
        invitations_received: pendingReceivedPage > 0 ? pendingReceivedTotal : (stats?.pending_received  ?? 0),
        invitations_sent:     pendingSentPage     > 0 ? pendingSentTotal     : (stats?.pending_sent      ?? 0),
        new_this_month:
            connections.filter(
                (c) =>
                    c.accepted_at &&
                    new Date(c.accepted_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            ).length,
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="grid">
                <div className="col-12">
                    {/* Page header */}
                    <div className="mb-3">
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">My Network</h1>
                        <p className="text-600 text-sm m-0">
                            Connections, invitations, and agents worth knowing.
                        </p>
                    </div>

                    {/* ── 1. FIND AGENTS BAR — sticky search input + cross-tenant
                            results panel (extracted to ./sections/FindAgentsBar). */}
                    <FindAgentsBar
                        wrapperRef={searchBarWrapperRef}
                        topPx={stickyOffsets.searchTop}
                        findSearch={findSearch}
                        onFindSearchChange={setFindSearch}
                        searching={searching}
                        searchedQuery={searchedQuery}
                        searchResults={searchResults}
                        actionLoading={actionLoading}
                        onConnect={handleSuggestionConnect}
                        onView={(id) => navigate(`/app/profile/${id}`)}
                    />

                    {/* ── 2. MAIN GRID: sidebar (left, lg+) + main column ──── */}
                    <div className="grid">
                        {/* SIDEBAR — left rail on desktop (lg+), hidden on mobile.
                            Extracted to ./sections/ManageNetworkSidebar. */}
                        <ManageNetworkSidebar
                            topPx={stickyOffsets.sidebarTop}
                            stats={[
                                { label: 'Connections',         value: statValues.connections,                  onClick: () => scrollTo('connections-section') },
                                { label: 'Pending Invitations', value: statValues.invitations_received,         onClick: () => scrollTo('pending-section') },
                                { label: 'Sent Requests',       value: statValues.invitations_sent,             onClick: () => { setPendingTabIndex(1); setTimeout(() => scrollTo('pending-section'), 0); } },
                                { label: 'Profile Views',       value: viewerStats?.total_views ?? 0 },
                                { label: 'Views (7 days)',      value: viewerStats?.views_last_7_days ?? 0 },
                                { label: 'New This Month',      value: statValues.new_this_month },
                            ]}
                        />

                        {/* MAIN COLUMN — right side on desktop, full width on mobile.
                            Order matches the reference: Pending Invitations →
                            People You May Know → My Connections. */}
                        <div className="col-12 lg:col-8">
                            {/* Pending invitations — Received | Sent TabView with
                                server-paginated infinite scroll. Extracted to
                                ./sections/PendingInvitationsCard. */}
                            <PendingInvitationsCard
                                pendingCardRef={pendingCardRef}
                                pendingTabIndex={pendingTabIndex}
                                onTabChange={setPendingTabIndex}

                                pendingReceived={pendingReceived}
                                pendingReceivedTotal={pendingReceivedTotal}
                                pendingReceivedLoading={pendingReceivedLoading}
                                pendingReceivedLoadingMore={pendingReceivedLoadingMore}
                                pendingReceivedHasMore={pendingReceivedHasMore}
                                receivedExpanded={receivedExpanded}
                                onReceivedExpand={() => setReceivedExpanded(true)}
                                onReceivedCollapse={() => setReceivedExpanded(false)}
                                receivedScrollRef={receivedScrollRef}
                                pendingReceivedSentinelRef={pendingReceivedSentinelRef}

                                pendingSent={pendingSent}
                                pendingSentTotal={pendingSentTotal}
                                pendingSentLoading={pendingSentLoading}
                                pendingSentLoadingMore={pendingSentLoadingMore}
                                pendingSentHasMore={pendingSentHasMore}
                                sentExpanded={sentExpanded}
                                onSentExpand={() => setSentExpanded(true)}
                                onSentCollapse={() => setSentExpanded(false)}
                                sentScrollRef={sentScrollRef}
                                pendingSentSentinelRef={pendingSentSentinelRef}

                                connections={connections}
                                actionLoading={actionLoading}
                                onAccept={handleAccept}
                                onReject={handleReject}
                                onWithdraw={handleCancelSent}
                                onView={(profileId) =>
                                    profileId && navigate(`/app/profile/${profileId}`)
                                }
                            />

                            {/* People you may know — 4-col grid of cover-banner
                                suggestion tiles. Extracted to
                                ./sections/PeopleYouMayKnowCard. */}
                            <PeopleYouMayKnowCard
                                suggestions={visibleSuggestions}
                                connections={connections}
                                actionLoading={actionLoading}
                                suggestionScope={suggestionScope}
                                onScopeChange={setSuggestionScope}
                                onConnect={handleSuggestionConnect}
                                onDismiss={handleSuggestionDismiss}
                                onView={(id) => id && navigate(`/app/profile/${id}`)}
                            />

                            {/* My connections — search + sort + filter chips +
                                paginated list/grid. Extracted to
                                ./sections/MyConnectionsCard. */}
                            <MyConnectionsCard
                                connections={connections}
                                connectionsTotal={connectionsTotal}
                                connectionsLoading={connectionsLoading}
                                connectionsHasMore={connectionsHasMore}
                                connectionsLoadingMore={connectionsLoadingMore}
                                connectionsScrollRef={connectionsScrollRef}
                                connectionsSentinelRef={connectionsSentinelRef}
                                connectionsCardRef={connectionsCardRef}
                                connectionsExpanded={connectionsExpanded}
                                onExpand={() => setConnectionsExpanded(true)}
                                onCollapse={() => setConnectionsExpanded(false)}
                                connectionsSearch={connectionsSearch}
                                onSearchChange={setConnectionsSearch}
                                sortBy={sortBy}
                                onSortChange={setSortBy}
                                activeFilters={activeFilters}
                                onToggleFilter={toggleFilter}
                                onClearFilters={clearFilters}
                                viewMode={viewMode}
                                onViewModeChange={setViewMode}
                                onView={(profileId) =>
                                    profileId && navigate(`/app/profile/${profileId}`)
                                }
                                onMessage={(profileId) =>
                                    navigate(`/app/messaging?profileId=${profileId}`)
                                }
                            />

                        </div>

                    </div>
                </div>
            </div>

            {/* The dedicated "Invitations you've sent" Dialog was removed —
                sent invitations now render inline under the "Sent" tab of the
                Pending Invitations card, so a popup dialog is redundant. The
                Withdraw flow is wired up via SentInvitationRow. */}
        </>
    );
};

export default MyNetwork;
